"use client";

import {
  useMemo,
  useState,
  useEffect,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "@/app/components/ui/sonner";
import { Button } from "@/app/components/ui/button";
import {
  ContactCheckbox,
  ContactMileageInput,
  ContactPhoneInput,
  ContactSelect,
  ContactTextInput,
  ContactTextareaInput,
} from "./contact-form-controls";
import { ControlledFileInput } from "./controlled-file-input";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronUp } from "lucide-react";
import type {
  ContactFormConfigT,
  ContactFormFieldConfigT,
  ContactFormSelectOptionT,
} from "./contact-form-types";
import type {
  DefaultValues,
  FieldPath,
  FieldValues,
  Resolver,
} from "react-hook-form";
import type { ZodSchema } from "zod";

const CONTACT_FORM_BODY_LIMIT_ERROR_PATTERN = /body exceeded \d+mb limit/i;

type ContactFormSubmitResultT = {
  success: boolean;
  errorMessage?: string;
};

export type ContactFormSubmitMetaT = {
  startedAtMs: number;
};

type FormSectionsProps<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
> = {
  config: ContactFormConfigT<TFieldName>;
  expandedSections: Record<string, boolean>;
  toggleSection: (sectionId: string) => void;
  renderField: (
    field: ContactFormFieldConfigT<TFieldName>,
    sectionId: string
  ) => ReactNode;
};

/**
 * Internal component that renders form sections with conditional rendering support.
 * Must be used inside FormProvider to access useWatch.
 */
function FormSections<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
>({
  config,
  expandedSections,
  toggleSection,
  renderField,
}: FormSectionsProps<TFormValues, TFieldName>) {
  // Collect all unique conditional render field names
  const conditionalFields = useMemo(
    () =>
      Array.from(
        new Set(
          config.sections
            .filter((section) => section.conditionalRender)
            .map((section) => section.conditionalRender!.fieldName)
        )
      ),
    [config.sections]
  );

  const watchedConditionalValues = useWatch({
    name: conditionalFields as FieldPath<TFormValues>[],
  });

  const conditionalValuesMap = useMemo(() => {
    const map = new Map<TFieldName, unknown>();

    if (conditionalFields.length === 0) {
      return map;
    }

    if (Array.isArray(watchedConditionalValues)) {
      conditionalFields.forEach((fieldName, index) => {
        map.set(fieldName as TFieldName, watchedConditionalValues[index]);
      });
      return map;
    }

    conditionalFields.forEach((fieldName) => {
      map.set(fieldName as TFieldName, watchedConditionalValues);
    });

    return map;
  }, [conditionalFields, watchedConditionalValues]);

  return (
    <>
      {config.sections.map((section) => {
        // Check conditional rendering
        const conditionalValue = section.conditionalRender
          ? conditionalValuesMap.get(section.conditionalRender.fieldName)
          : undefined;

        const shouldRender =
          !section.conditionalRender ||
          section.conditionalRender.condition(conditionalValue);

        if (!shouldRender) {
          return null;
        }

        const isCollapsible = section.collapsible ?? false;
        const isExpanded = isCollapsible
          ? (expandedSections[section.id] ?? section.defaultExpanded ?? false)
          : true;

        const hasHeader = section.title || section.description || isCollapsible;

        return (
          <div key={section.id} className={hasHeader ? "space-y-4" : ""}>
            {hasHeader && (
              <div
                className={cn(
                  "flex items-center justify-between rounded-md transition-colors",
                  isCollapsible &&
                    "hover:bg-muted/50 -mx-4 -my-3 cursor-pointer px-4 py-3"
                )}
                onClick={
                  isCollapsible ? () => toggleSection(section.id) : undefined
                }
              >
                <div>
                  {section.title && (
                    <h3 className="text-base font-semibold">{section.title}</h3>
                  )}
                  {section.description && (
                    <p className="text-muted-foreground text-sm">
                      {section.description}
                    </p>
                  )}
                </div>
                {isCollapsible && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="hover:bg-background h-8 w-8 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSection(section.id);
                    }}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-6 w-6" />
                    ) : (
                      <ChevronDown className="h-6 w-6" />
                    )}
                  </Button>
                )}
              </div>
            )}

            {(!isCollapsible || isExpanded) && (
              <div
                className={cn(
                  section.layout === "stack"
                    ? "space-y-6"
                    : "grid grid-cols-1 gap-x-6 gap-y-6 md:grid-cols-2",
                  isCollapsible && isExpanded && "pt-7"
                )}
              >
                {section.fields.map((field) => renderField(field, section.id))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

type ContactFormProps<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
> = {
  schema: ZodSchema<TFormValues>;
  defaultValues: DefaultValues<TFormValues>;
  config: ContactFormConfigT<TFieldName>;
  selectFieldNames: readonly TSelectFieldName[];
  onSubmit: (
    values: TFormValues,
    meta: ContactFormSubmitMetaT
  ) => Promise<ContactFormSubmitResultT>;
  className?: string;
  runtimeSelectOptions?: Partial<
    Record<TSelectFieldName, ContactFormSelectOptionT[]>
  >;
  submitButtonVariant?: "default" | "secondary" | "outline";
  mode?: "onBlur" | "onChange" | "onSubmit" | "all";
  header?: ReactNode;
  presentation?: "card" | "plain";
  renderFooter?: (params: {
    isSubmitting: boolean;
    submitButton: ReactNode;
  }) => ReactNode;
};

/**
 * Generic contact form component that renders fields based on configuration.
 * Handles validation, honeypot protection, submission state, and toast feedback.
 */
export function ContactForm<
  TFormValues extends FieldValues,
  TFieldName extends FieldPath<TFormValues> & string,
  TSelectFieldName extends TFieldName,
>({
  schema,
  defaultValues,
  config,
  selectFieldNames,
  onSubmit,
  className,
  runtimeSelectOptions,
  submitButtonVariant = "default",
  mode = "onBlur",
  header,
  presentation = "card",
  renderFooter,
}: ContactFormProps<TFormValues, TFieldName, TSelectFieldName>) {
  const resolver = useMemo(
    () => zodResolver(schema as never) as Resolver<TFormValues>,
    [schema]
  );

  const form = useForm<TFormValues>({
    resolver,
    defaultValues,
    mode,
  });

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { isSubmitting },
  } = form;

  const [honeypotInputValue, setHoneypotInputValue] = useState("");
  const [formStartedAtMs] = useState(() => Date.now());

  // Set URL field automatically on mount
  useEffect(() => {
    if (typeof window !== "undefined" && window.location) {
      try {
        const currentUrl = window.location.href;
        setValue("URL" as FieldPath<TFormValues>, currentUrl as never);
      } catch {
        // SecurityError catch - access to location.href is blocked
        // URL field will remain undefined/null
      }
    }
  }, [setValue]);

  const collapsibleStates = useMemo(() => {
    return config.sections
      .filter((section) => section.collapsible)
      .reduce<Record<string, boolean>>((acc, section) => {
        acc[section.id] = section.defaultExpanded ?? false;
        return acc;
      }, {});
  }, [config.sections]);

  const [expandedSections, setExpandedSections] = useState(collapsibleStates);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const selectFieldNameSet = useMemo(
    () => new Set(selectFieldNames),
    [selectFieldNames]
  );

  // Watch all form values for disabled function evaluation
  const allFormValues = useWatch({ control });

  // Watch make field for dynamic contact person filtering (leasingvertrag variant)
  const makeValue = useWatch({
    name: "make" as FieldPath<TFormValues>,
    control,
  });

  const resolveSelectOptions = (field: ContactFormFieldConfigT<TFieldName>) => {
    if (field.type !== "select") {
      return [];
    }

    if (!selectFieldNameSet.has(field.name as TSelectFieldName)) {
      return field.options ?? [];
    }

    const options =
      runtimeSelectOptions?.[field.name as TSelectFieldName] ??
      field.options ??
      [];

    // For contactPerson field, filter options based on selected make (leasingvertrag variant)
    if (field.name === "contactPerson" && makeValue) {
      const filtered = options.filter((option) => {
        // Check if option has makeIds metadata and if selected make is included
        const optionWithMetadata = option as ContactFormSelectOptionT & {
          makeIds?: string[];
          departmentIds?: string[];
        };
        // Filter by make: person must have the selected make
        const hasMake =
          !optionWithMetadata.makeIds ||
          optionWithMetadata.makeIds.length === 0 ||
          optionWithMetadata.makeIds.includes(makeValue as string);
        // Note: Department filtering is already done in the page component
        // (only Verkauf and Gebrauchtwagen are included in the options)
        return hasMake;
      });
      // Sort filtered options alphabetically by label
      return filtered.sort((a, b) => a.label.localeCompare(b.label, "de"));
    }

    return options;
  };

  const resolveDisabled = (
    field: ContactFormFieldConfigT<TFieldName>
  ): boolean => {
    if (field.disabled === undefined) {
      return false;
    }

    if (typeof field.disabled === "boolean") {
      return field.disabled;
    }

    // Evaluate function with current form values
    return field.disabled(allFormValues as Record<string, unknown>);
  };

  const safeSubmit = handleSubmit(async (data) => {
    const honeypotField = config.honeypotFieldName as keyof TFormValues;
    const honeypotValue = data[honeypotField];
    void honeypotValue;

    if (typeof honeypotValue === "string" && honeypotValue.trim().length > 0) {
      // Simulate success on bot detection to avoid signalling failure
      toast.success(config.successToast?.title ?? "Anfrage gesendet");
      reset(defaultValues);
      setHoneypotInputValue("");
      // Reset URL after reset
      if (typeof window !== "undefined" && window.location) {
        try {
          setValue(
            "URL" as FieldPath<TFormValues>,
            window.location.href as never
          );
        } catch {
          // SecurityError catch - access to location.href is blocked
          // URL field will remain undefined/null
        }
      }
      return;
    }

    try {
      const result = await onSubmit(data, {
        startedAtMs: formStartedAtMs,
      });

      if (result.success) {
        toast.success(config.successToast?.title ?? "Anfrage gesendet", {
          description: config.successToast?.description,
        });
        reset(defaultValues);
        // Reset URL after reset
        if (typeof window !== "undefined" && window.location) {
          try {
            setValue(
              "URL" as FieldPath<TFormValues>,
              window.location.href as never
            );
          } catch {
            // SecurityError catch - access to location.href is blocked
            // URL field will remain undefined/null
          }
        }
        setHoneypotInputValue("");
      } else {
        toast.error(config.errorToast?.title ?? "Fehler beim Senden", {
          description:
            result.errorMessage ?? config.errorToast?.description ?? "",
        });
      }
    } catch (error) {
      console.error("Contact form submission error:", error);

      if (error instanceof Error) {
        if (
          CONTACT_FORM_BODY_LIMIT_ERROR_PATTERN.test(error.message) ||
          error.message.toLowerCase().includes("request entity too large")
        ) {
          toast.error("Datei zu groß", {
            description:
              "Eine hochgeladene Datei ist zu groß. Bitte wählen Sie eine kleinere Datei aus.",
          });
          return;
        }

        // Check if it's a network error
        if (
          error.message.includes("Failed to fetch") ||
          error.message.includes("fetch failed") ||
          error.message.includes("network") ||
          error.message.includes("NetworkError") ||
          error.name === "TypeError"
        ) {
          toast.error(config.errorToast?.title ?? "Fehler beim Senden", {
            description:
              "Keine Internetverbindung. Bitte überprüfen Sie Ihre Netzwerkverbindung und versuchen Sie es erneut.",
          });
          return;
        }
      }

      // Generic error fallback
      toast.error(config.errorToast?.title ?? "Fehler beim Senden", {
        description:
          config.errorToast?.description ??
          "Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
      });
    }
  });

  const renderField = (
    field: ContactFormFieldConfigT<TFieldName>,
    sectionId: string
  ) => {
    // Skip hidden fields
    if (field.hidden) {
      return null;
    }

    const colSpanClass =
      field.colSpan === 2 ? "md:col-span-2" : "md:col-span-1";

    if (field.type === "honeypot") {
      const honeypotDomName = "website";

      const handleHoneypotChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;
        setHoneypotInputValue(value);
        setValue(field.name as FieldPath<TFormValues>, value as never, {
          shouldDirty: true,
        });
      };

      return (
        <div
          key={`${sectionId}-${field.name}`}
          style={{
            position: "absolute",
            left: "-9999px",
            width: "1px",
            height: "1px",
            overflow: "hidden",
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <input
            {...register(field.name as FieldPath<TFormValues>)}
            type="hidden"
          />
          <input
            id={honeypotDomName}
            name={honeypotDomName}
            value={honeypotInputValue}
            onChange={handleHoneypotChange}
            type="text"
            autoComplete="off"
            tabIndex={-1}
            placeholder=""
            inputMode="text"
            spellCheck={false}
            data-testid={`honeypot-${String(field.name)}`}
            data-form-type="other"
            data-lpignore="true"
            aria-hidden="true"
          />
        </div>
      );
    }

    if (field.type === "checkbox") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn(
            "flex items-start",
            typeof field.label === "string" ? "space-x-3" : "",
            colSpanClass
          )}
        >
          <ContactCheckbox name={field.name}>{field.label}</ContactCheckbox>
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn("flex flex-col gap-2", colSpanClass, field.className)}
        >
          <ContactTextareaInput
            name={field.name}
            label={typeof field.label === "string" ? field.label : ""}
            placeholder={field.placeholder}
            labelClassName="text-md font-semibold"
            className="py-2.5 text-sm sm:py-3"
          />
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
        </div>
      );
    }

    if (field.type === "select") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn("flex flex-col gap-2", colSpanClass, field.className)}
        >
          <ContactSelect
            name={field.name}
            label={typeof field.label === "string" ? field.label : undefined}
            options={resolveSelectOptions(field)}
            placeholder={field.placeholder}
            required={field.required}
            disabled={resolveDisabled(field)}
            labelClassName="text-md font-semibold"
            triggerClassName="py-2.5 text-sm sm:py-3"
          />
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
        </div>
      );
    }

    if (field.type === "mileage") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn("flex flex-col gap-2", colSpanClass, field.className)}
        >
          <ContactMileageInput
            name={field.name}
            label={typeof field.label === "string" ? field.label : ""}
            placeholder={field.placeholder}
            required={field.required}
            unit={field.unit}
            labelClassName="text-md font-semibold"
            className="py-2.5 text-sm sm:py-3"
          />
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
        </div>
      );
    }

    if (field.type === "phone") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn("flex flex-col gap-2", colSpanClass, field.className)}
        >
          <ContactPhoneInput
            name={field.name}
            label={typeof field.label === "string" ? field.label : ""}
            placeholder={field.placeholder}
            required={field.required}
            labelClassName="text-md font-semibold"
            className="py-2.5 text-sm sm:py-3"
          />
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
        </div>
      );
    }

    if (field.type === "file") {
      return (
        <div
          key={`${sectionId}-${field.name}`}
          className={cn("flex flex-col gap-2", colSpanClass, field.className)}
        >
          <ControlledFileInput
            name={field.name}
            label={typeof field.label === "string" ? field.label : undefined}
            buttonLabel={field.buttonLabel}
            accept={field.accept}
            maxSize={field.maxSize}
            required={field.required}
          />
          {field.description && (
            <p className="text-muted-foreground text-sm">{field.description}</p>
          )}
        </div>
      );
    }

    return (
      <div
        key={`${sectionId}-${field.name}`}
        className={cn("flex flex-col gap-2", colSpanClass, field.className)}
      >
        <ContactTextInput
          name={field.name}
          label={typeof field.label === "string" ? field.label : ""}
          placeholder={field.placeholder}
          required={field.required}
          type={field.inputType ?? "text"}
          labelClassName="text-md font-semibold"
          className="py-2.5 text-base placeholder:text-base sm:py-3"
        />
        {field.description && (
          <p className="text-muted-foreground text-sm">{field.description}</p>
        )}
      </div>
    );
  };

  const containerBaseClasses =
    presentation === "plain"
      ? "space-y-8"
      : "bg-background space-y-8 rounded-lg p-6 shadow-lg lg:p-8";

  const submitButton = (
    <Button type="submit" variant={submitButtonVariant} disabled={isSubmitting}>
      {isSubmitting
        ? (config.submittingLabel ?? "Wird gesendet...")
        : (config.submitButtonLabel ?? "Nachricht senden")}
    </Button>
  );

  return (
    <FormProvider {...form}>
      <div className={cn(containerBaseClasses, className)}>
        {header}
        <form className="space-y-8" onSubmit={safeSubmit}>
          <FormSections
            config={config}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            renderField={renderField}
          />

          {renderFooter ? (
            renderFooter({ isSubmitting, submitButton })
          ) : (
            <div className="flex justify-end">{submitButton}</div>
          )}
        </form>
      </div>
    </FormProvider>
  );
}
